import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { FileText, Plus, Pencil, Trash2, Loader2, Search, ChevronLeft, ChevronRight, X, ArrowUp, ArrowDown } from "lucide-react";
import {
  useTabs,
  useCreateTab,
  useUpdateTab,
  useDeleteTab,
  type Tab,
} from "@api/tabs";
import { TabForm } from "@components/TabForm";
import { t } from "@lib/i18n";

const PAGE_SIZE = 20;

export function Tabs() {
  const [isCreating, setIsCreating] = useState(false);
  const [editingTab, setEditingTab] = useState<Tab | null>(null);
  const [viewingTab, setViewingTab] = useState<Tab | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  
  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page when search changes
    }, 300);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);
  
  const { data: tabsData, isLoading, error } = useTabs({
    page,
    page_size: PAGE_SIZE,
    search: debouncedSearch || undefined,
    sort_by: sortBy,
    sort_order: sortOrder,
  });

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    setPage(1); // Reset to first page when sorting changes
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-3 h-3 ml-1 inline" />
    ) : (
      <ArrowDown className="w-3 h-3 ml-1 inline" />
    );
  };
  
  const createMutation = useCreateTab();
  const updateMutation = useUpdateTab();
  const deleteMutation = useDeleteTab();

  const handleCreate = (data: Parameters<typeof createMutation.mutate>[0]) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setIsCreating(false);
        // Reset to first page to see the new tab
        setPage(1);
      },
    });
  };

  const handleUpdate = (data: Parameters<typeof updateMutation.mutate>[0]["payload"]) => {
    if (editingTab) {
      updateMutation.mutate(
        { id: editingTab.id, payload: data },
        {
          onSuccess: () => {
            setEditingTab(null);
          },
        }
      );
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(t("tabs.deleteConfirm"))) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">{t("tabs.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {t("tabs.description")}
            </p>
          </div>
        </div>
        <Button onClick={() => setIsCreating(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          {t("tabs.createTab")}
        </Button>
      </div>

      {/* Tab Detail View */}
      {viewingTab && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setViewingTab(null)}
          />
          <Card className="fixed inset-2 z-50 overflow-auto bg-background shadow-2xl w-[calc(100%-1rem)]">
          <CardHeader className="sticky top-0 bg-background border-b z-10">
            <div className="flex items-center justify-between">
              <CardTitle>{viewingTab.title}</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setViewingTab(null)}
                className="gap-1"
              >
                <X className="w-4 h-4" />
                {t("common.close")}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">{t("common.id")}:</span>
                <span className="ml-2 font-medium">{viewingTab.id}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t("tabs.artistLabel")}:</span>
                <span className="ml-2 font-medium">{viewingTab.artist?.name || "-"}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t("tabs.userLabel")}:</span>
                <span className="ml-2 font-medium">{viewingTab.user_email || `User #${viewingTab.user_id}`}</span>
              </div>
              {viewingTab.created_at && (
                <div>
                  <span className="text-muted-foreground">{t("common.createdAt")}:</span>
                  <span className="ml-2 font-medium">
                    {new Date(viewingTab.created_at).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
            
            <div>
              <h3 className="text-sm font-medium mb-2">{t("tabs.bodyLabel")}</h3>
              <div className="bg-muted p-4 rounded-md">
                <pre className="whitespace-pre-wrap font-mono text-sm">{viewingTab.body}</pre>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setViewingTab(null);
                  setEditingTab(viewingTab);
                }}
                className="gap-1"
              >
                <Pencil className="w-4 h-4" />
                {t("common.edit")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setViewingTab(null)}
              >
                {t("common.close")}
              </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}

      {/* Create/Edit Form Modal */}
      {(isCreating || editingTab) && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => {
              setIsCreating(false);
              setEditingTab(null);
            }}
          />
          <Card className="fixed inset-2 z-50 flex flex-col bg-background shadow-2xl w-[calc(100%-1rem)] h-[calc(100vh-1rem)]">
            <CardHeader className="flex-shrink-0 border-b">
              <div className="flex items-center justify-between">
                <CardTitle>{editingTab ? t("tabs.editTab") : t("tabs.createTab")}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setEditingTab(null);
                  }}
                  className="gap-1"
                >
                  <X className="w-4 h-4" />
                  {t("common.close")}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto pt-6 flex flex-col min-h-0">
              <TabForm
                key={editingTab?.id || "create"}
                initialData={editingTab ? {
                  artist_id: editingTab.artist_id,
                  title: editingTab.title,
                  body: editingTab.body,
                  body_rendered: editingTab.body_rendered,
                  user_id: editingTab.user_id,
                } : undefined}
                onSubmit={editingTab ? handleUpdate : handleCreate}
                onCancel={() => {
                  setIsCreating(false);
                  setEditingTab(null);
                }}
                isLoading={createMutation.isPending || updateMutation.isPending}
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Tabs List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{t("tabs.tabsList")}</CardTitle>
            {tabsData && (
              <div className="text-sm text-muted-foreground">
                {t("common.showing")} {((page - 1) * PAGE_SIZE) + 1}-{Math.min(page * PAGE_SIZE, tabsData.total)} {t("common.of")} {tabsData.total}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t("common.searchPlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="text-sm text-destructive py-4">
              {t("tabs.errorLoading")}: {error instanceof Error ? error.message : t("common.unknownError")}
            </div>
          )}

          {!isLoading && !error && tabsData && tabsData.list.length === 0 && (
            <p className="text-muted-foreground py-4">{t("tabs.noTabs")}</p>
          )}

          {!isLoading && !error && tabsData && tabsData.list.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("id")}
                      >
                        {t("common.id")}
                        <SortIcon column="id" />
                      </th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("title")}
                      >
                        {t("tabs.titleLabel")}
                        <SortIcon column="title" />
                      </th>
                      <th className="text-left p-2 font-medium">{t("tabs.artistLabel")}</th>
                      <th className="text-left p-2 font-medium">{t("tabs.userLabel")}</th>
                      <th 
                        className="text-left p-2 font-medium cursor-pointer hover:bg-muted/50 select-none"
                        onClick={() => handleSort("created_at")}
                      >
                        {t("common.createdAt")}
                        <SortIcon column="created_at" />
                      </th>
                      <th className="text-right p-2 font-medium">{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tabsData.list.map((tab) => (
                      <tr 
                        key={tab.id} 
                        className="border-b hover:bg-muted/50 cursor-pointer"
                        onClick={() => setViewingTab(tab)}
                      >
                        <td className="p-2">{tab.id}</td>
                        <td className="p-2 font-medium">{tab.title}</td>
                        <td className="p-2 text-muted-foreground">{tab.artist?.name || "-"}</td>
                        <td className="p-2 text-muted-foreground">{tab.user_email || `User #${tab.user_id}`}</td>
                        <td className="p-2 text-muted-foreground text-sm">
                          {tab.created_at ? new Date(tab.created_at).toLocaleDateString() : "-"}
                        </td>
                        <td className="p-2" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setEditingTab(tab)}
                              className="gap-1"
                            >
                              <Pencil className="w-4 h-4" />
                              {t("common.edit")}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(tab.id)}
                              disabled={deleteMutation.isPending}
                              className="gap-1 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                              {t("common.delete")}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {tabsData.total_pages > 1 && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="text-sm text-muted-foreground">
                    {t("common.page")} {tabsData.page} {t("common.of")} {tabsData.total_pages}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1 || isLoading}
                      className="gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      {t("common.previous")}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(tabsData.total_pages, p + 1))}
                      disabled={page === tabsData.total_pages || isLoading}
                      className="gap-1"
                    >
                      {t("common.next")}
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

